import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Save, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/account/profiel")({
  head: () => ({
    meta: [{ title: "Profiel — Elvion Group Nederland" }, { name: "robots", content: "noindex" }],
  }),
  component: ProfilePage,
});

function ProfilePage() {
  const { user, profile, refresh } = useAuth();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    setFullName(profile?.full_name ?? "");
    setPhone(profile?.phone ?? "");
  }, [profile]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName.trim() || null, phone: phone.trim() || null })
      .eq("id", user!.id);
    setSaving(false);
    if (error) {
      setMsg({ kind: "err", text: error.message });
      return;
    }
    setMsg({ kind: "ok", text: "Profiel bijgewerkt." });
    await refresh();
    setTimeout(() => setMsg(null), 2500);
  };

  return (
    <div className="space-y-8">
      <PageHeader
        icon={User}
        title="Profiel"
        subtitle="Beheer uw persoonlijke gegevens en contactinformatie."
      />

      <form
        onSubmit={save}
        className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Volledige naam">
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-[#3a7bd5] focus:outline-none focus:ring-2 focus:ring-[#3a7bd5]/20"
              placeholder="Naam en achternaam"
            />
          </Field>
          <Field label="E-mailadres">
            <input
              type="email"
              value={user?.email ?? ""}
              disabled
              className="w-full cursor-not-allowed rounded-lg border border-border bg-muted px-3 py-2.5 text-sm text-text-secondary"
            />
          </Field>
          <Field label="Telefoonnummer">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:border-[#3a7bd5] focus:outline-none focus:ring-2 focus:ring-[#3a7bd5]/20"
              placeholder="+31 6 12345678"
            />
          </Field>
          <Field label="Accounttype">
            <input
              type="text"
              value={profile?.customer_type === "business" ? "Zakelijk" : "Particulier"}
              disabled
              className="w-full cursor-not-allowed rounded-lg border border-border bg-muted px-3 py-2.5 text-sm capitalize text-text-secondary"
            />
          </Field>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-5">
          {msg ? (
            <p
              className={`text-sm font-medium ${
                msg.kind === "ok" ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {msg.text}
            </p>
          ) : (
            <span className="text-xs text-text-secondary">
              Wijzigingen worden direct opgeslagen.
            </span>
          )}
          <Button type="submit" disabled={saving} className="gap-2">
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Opslaan
          </Button>
        </div>
      </form>
    </div>
  );
}

export function PageHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0b2a4a] to-[#3a7bd5] text-white shadow-lg">
        <Icon className="size-5" />
      </div>
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        <p className="mt-1 text-sm text-text-secondary">{subtitle}</p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
